import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Paper, Typography, TextField, Grid, Button, Chip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Tabs, Tab, Checkbox, FormControlLabel, ButtonGroup, Alert,
} from "@mui/material";
import api from "../services/api";
import { getUser } from "../services/auth";

// Generate a strong, unambiguous password (avoids breach warnings).
function genPassword() {
  const letters = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%&*";
  const all = letters + digits + symbols;
  const rnd = new Uint32Array(14);
  (window.crypto || window.msCrypto).getRandomValues(rnd);
  let pw = "";
  for (let i = 0; i < 12; i++) pw += all[rnd[i] % all.length];
  // guarantee at least one digit and one symbol
  pw += digits[rnd[12] % digits.length];
  pw += symbols[rnd[13] % symbols.length];
  return pw;
}

const ROLE_DESCRIPTIONS = {
  admin:      "Full access including user management",
  manager:    "Full access except user management",
  accountant: "Invoices, Reports, General Ledger, Daily Cash, Accounts",
  cashier:    "Daily Cash only",
  yard:       "Purchase Invoice, Intake, Packing List, Finished Goods, Regrade",
  sales:      "Sale Orders, Sale Invoices, Bookings, Customers",
  viewer:     "Read-only across everything",
  employee:   "Legacy broad access (treat like manager)",
};

const roleColor = (role) => ({
  admin:      "error",
  manager:    "primary",
  accountant: "success",
  cashier:    "warning",
  yard:       "info",
  sales:      "secondary",
  viewer:     "default",
  employee:   "default",
}[role] || "default");

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: "", password: "", role: "employee" });
  const [resetUser, setResetUser] = useState(null); // user whose password is being reset
  const me = getUser();

  const refresh = useCallback(() => {
    api.get("/users")
      .then((r) => setUsers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load users:", e));
  }, []);

  useEffect(() => {
    refresh();
    api.get("/users/roles")
      .then((r) => setRoles(Array.isArray(r.data?.roles) ? r.data.roles : []))
      .catch((e) => console.error("Failed to load roles:", e));
  }, [refresh]);

  const startAdd = () => {
    setEditing(null);
    setForm({ username: "", password: "", role: "employee" });
    setAddOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({ username: u.username, password: "", role: u.role || "employee" });
    setAddOpen(true);
  };

  const save = async () => {
    if (!editing && (!form.username || !form.password)) {
      alert("Username and password are required for new users.");
      return;
    }
    if (!form.role) { alert("Pick a role."); return; }
    try {
      if (editing) {
        const body = { role: form.role };
        if (form.password) body.password = form.password;
        await api.put(`/users/${editing.id}`, body);
      } else {
        await api.post("/users", form);
      }
      setAddOpen(false);
      refresh();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.error || err.message));
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      refresh();
    } catch (err) {
      alert("Delete failed: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={2}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>User Management</Typography>
        <Button variant="contained" onClick={startAdd}>+ Add User</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Role cheat sheet</Typography>
        <Grid container spacing={1}>
          {Object.entries(ROLE_DESCRIPTIONS).map(([r, desc]) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={r}>
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                <Chip size="small" color={roleColor(r)} label={r} sx={{ minWidth: 80 }} />
                <Typography variant="body2" sx={{ color: "text.secondary" }}>{desc}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Role</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell>{u.id}</TableCell>
                <TableCell>{u.username}</TableCell>
                <TableCell>
                  <Chip size="small" color={roleColor(u.role)} label={u.role || "employee"} />
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" variant="outlined" onClick={() => startEdit(u)} sx={{ mr: 1 }}>
                    Edit
                  </Button>
                  {/* Reset password — hidden for the platform owner and for your own row */}
                  {!u.IsPlatformOwner && u.id !== me?.id && (
                    <Button size="small" variant="outlined" color="secondary" onClick={() => setResetUser(u)} sx={{ mr: 1 }}>
                      Reset Password
                    </Button>
                  )}
                  <Button size="small" variant="outlined" color="error" onClick={() => del(u)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <UserDialog
        open={addOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        roles={roles}
        onClose={() => setAddOpen(false)}
        onSave={save}
      />

      <ResetPasswordDialog
        user={resetUser}
        onClose={() => setResetUser(null)}
      />
    </Box>
  );
}

/* =====================================================
 * Reset a user's password. Passwords are stored hashed and can never be
 * read back, so this SETS a new one. The value is shown only here, once,
 * so the SaaS admin can copy it and hand it to that company.
 * ===================================================== */
function ResetPasswordDialog({ user, onClose }) {
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // Reset local state whenever a different user is opened.
  useEffect(() => {
    setPwd("");
    setShow(true);
    setSaving(false);
    setSaved(false);
    setCopied(false);
  }, [user]);

  if (!user) return null;

  const copy = async () => {
    try { await navigator.clipboard.writeText(pwd); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard may be blocked; user can select manually */ }
  };

  const save = async () => {
    if (!pwd || pwd.length < 6) { alert("Enter a password of at least 6 characters (or click Generate)."); return; }
    setSaving(true);
    try {
      await api.put(`/users/${user.id}`, { password: pwd });
      setSaved(true);
    } catch (err) {
      alert("Failed to reset password: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!user} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password — {user.username}</DialogTitle>
      <DialogContent>
        <Alert severity={saved ? "success" : "info"} sx={{ mb: 2 }}>
          {saved
            ? "Password saved. Copy it now and send it to the user — it can't be shown again."
            : "Type a new password or generate a strong one. The user logs in with this immediately."}
        </Alert>
        <TextField
          label="New password"
          type={show ? "text" : "password"}
          fullWidth
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          disabled={saved}
          autoFocus
        />
        <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
          {!saved && (
            <Button size="small" variant="outlined" onClick={() => { setPwd(genPassword()); setShow(true); }}>
              Generate strong
            </Button>
          )}
          <Button size="small" variant="outlined" onClick={() => setShow((s) => !s)}>
            {show ? "Hide" : "Show"}
          </Button>
          <Button size="small" variant="outlined" onClick={copy} disabled={!pwd}>
            {copied ? "Copied!" : "Copy"}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{saved ? "Done" : "Cancel"}</Button>
        {!saved && (
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save password"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/* =====================================================
 * Edit / Create dialog with two tabs:
 *   1. General  - username / password / role
 *   2. Modules  - per-user module checkboxes (only when editing existing user)
 * ===================================================== */
function UserDialog({ open, editing, form, setForm, roles, onClose, onSave }) {
  const [tab, setTab] = useState(0);
  const [modulesList, setModulesList] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [picked, setPicked] = useState(new Set());
  const [initialPicked, setInitialPicked] = useState(new Set());
  const [permLoading, setPermLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load the canonical module list once
  useEffect(() => {
    if (!open) return;
    api.get("/users/modules")
      .then((r) => {
        setModulesList(Array.isArray(r.data?.modules) ? r.data.modules : []);
        setRoleDefaults(r.data?.roleDefaults || {});
      })
      .catch((e) => console.error("Failed to load modules:", e));
  }, [open]);

  // Load the current user's permissions when editing
  useEffect(() => {
    if (!open || !editing) {
      setPicked(new Set());
      setInitialPicked(new Set());
      setTab(0);
      return;
    }
    setPermLoading(true);
    api.get(`/users/${editing.id}/permissions`)
      .then((r) => {
        // Explicit per-user overrides if any; otherwise pre-tick the user's
        // current effective modules (role defaults capped by the company) so
        // the admin sees what the user actually has, not a blank list.
        const explicit = Array.isArray(r.data?.modules) ? r.data.modules : [];
        const effective = Array.isArray(r.data?.effective) ? r.data.effective : [];
        const set = new Set(explicit.length ? explicit : effective);
        setPicked(set);
        setInitialPicked(set);
      })
      .catch((e) => console.error("Failed to load permissions:", e))
      .finally(() => setPermLoading(false));
  }, [open, editing]);

  const grouped = useMemo(() => {
    const g = {};
    for (const m of modulesList) {
      const k = m.group || "Other";
      if (!g[k]) g[k] = [];
      g[k].push(m);
    }
    return g;
  }, [modulesList]);

  const toggleKey = (k) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const applyRoleDefaults = (role) => {
    const keys = roleDefaults[role] || [];
    setPicked(new Set(keys));
  };

  const tickAll = () => setPicked(new Set(modulesList.map((m) => m.key)));
  const untickAll = () => setPicked(new Set());

  const dirty = useMemo(() => {
    if (!editing) return false;
    if (picked.size !== initialPicked.size) return true;
    for (const k of picked) if (!initialPicked.has(k)) return true;
    return false;
  }, [picked, initialPicked, editing]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Always save general info first (creates user if new)
      await onSave();

      // For new users we don't have an ID yet to set permissions; the
      // admin can edit them again to set per-user modules.
      // For existing users, only call setPermissions if anything changed.
      if (editing && dirty) {
        await api.put(`/users/${editing.id}/permissions`, {
          modules: Array.from(picked),
        });
      }
    } catch (err) {
      console.error("Save failed:", err);
      alert("Save failed: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{editing ? `Edit user "${editing.username}"` : "Add User"}</DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="General" />
          <Tab label={`Modules${editing ? "" : " (save first)"}`} disabled={!editing} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {!editing && (
              <TextField
                label="Username" fullWidth
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            )}
            <TextField
              label={editing ? "New password (leave blank to keep)" : "Password"}
              type="password" fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
            <TextField
              label="Role" select fullWidth
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              helperText="Role is the default starting point. Use the Modules tab to override per user."
            >
              {roles.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
          </Box>
        )}

        {tab === 1 && editing && (
          <Box sx={{ mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Tick the modules this user can access. If you tick at least one,
              the user sees <b>only</b> those modules — overriding the role.
              Untick all → user falls back to their role defaults.
              <br />
              <b>Admin</b> role always sees everything regardless of these
              ticks. The user must reload (or log out / back in) to see changes.
            </Alert>

            <Box sx={{ mb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>Quick set:</Typography>
              <ButtonGroup size="small" variant="outlined">
                {roles.map((r) => (
                  <Button key={r} onClick={() => applyRoleDefaults(r)}>
                    Apply {r}
                  </Button>
                ))}
              </ButtonGroup>
              <Button size="small" variant="outlined" onClick={tickAll}>Tick all</Button>
              <Button size="small" variant="outlined" onClick={untickAll}>Untick all</Button>
            </Box>

            {permLoading ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading…</Typography>
            ) : (
              Object.entries(grouped).map(([groupName, items]) => (
                <Paper key={groupName} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                  <Typography variant="overline" sx={{ color: "text.secondary" }}>{groupName}</Typography>
                  <Grid container spacing={0.5}>
                    {items.map((m) => (
                      <Grid size={{ xs: 12, sm: 6 }} key={m.key}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={picked.has(m.key)}
                              onChange={() => toggleKey(m.key)}
                              size="small"
                            />
                          }
                          label={m.label}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              ))
            )}

            <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
              {picked.size} of {modulesList.length} module{modulesList.length === 1 ? "" : "s"} ticked
              {picked.size === 0 && " — user will fall back to role defaults"}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {editing ? "Save changes" : "Create user"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
